import { cn } from "@/lib/utils"
import { CSSProperties, ReactElement, useEffect, useRef, useState } from "react"
import { motion, useInView } from "framer-motion"

interface AnimationPreset {
  hidden: Record<string, number | string>
  duration: number
}

const presets: Record<string, AnimationPreset> = {
  fadeIn: {
    hidden: { opacity: 0, y: 10 },
    duration: 0.4,
  },
  blurIn: {
    hidden: { opacity: 0, filter: "blur(10px)" },
    duration: 0.5,
  },
  blurInUp: {
    hidden: { opacity: 0, filter: "blur(10px)", y: 20 },
    duration: 0.5,
  },
  slideUp: {
    hidden: { opacity: 0, y: 20 },
    duration: 0.5,
  },
  slideLeft: {
    hidden: { opacity: 0, x: 20 },
    duration: 0.3,
  },
  scaleUp: {
    hidden: { opacity: 0, scale: 0.6 },
    duration: 0.5,
  },
}

type AnimationPresetName = keyof typeof presets

interface TextAnimateProps {
  children: string
  className?: string
  segmentClassName?: string
  as?: "span" | "div" | "p" | "h1" | "h2" | "h3"
  startOnView?: boolean
  once?: boolean
  by?: "text" | "word" | "character" | "line"
  animation?: AnimationPresetName
  delay?: number
  style?: CSSProperties
}

export function TextAnimate({
  children,
  className,
  segmentClassName,
  as = "span",
  startOnView = true,
  once = false,
  by = "word",
  animation = "fadeIn",
  delay = 0,
  style,
}: TextAnimateProps): ReactElement | null {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once, amount: 0.5 })
  const [hasAnimated, setHasAnimated] = useState(false)

  useEffect(() => {
    if (inView) setHasAnimated(true)
  }, [inView])

  const shouldAnimate = startOnView ? (inView || hasAnimated) : true
  const preset = presets[animation] || presets.fadeIn

  const segments = by === "word"
    ? children.split(/\s+/)
    : by === "character"
      ? children.split("")
      : by === "line"
        ? children.split("\n")
        : [children]

  const visibleStyle = { opacity: 1, y: 0, x: 0, scale: 1, filter: "blur(0px)" }

  const content = segments.map((segment, i) => (
    <motion.span
      key={`seg-${by}-${i}`}
      className={cn("inline-block", segmentClassName)}
      initial={false}
      animate={shouldAnimate ? visibleStyle : preset.hidden}
      transition={{
        delay: delay + i * 0.07,
        duration: preset.duration,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      {segment}
      {by === "word" && i < segments.length - 1 ? "\u00A0" : ""}
    </motion.span>
  ))

  const classes = cn(
    "flex flex-wrap",
    as !== "span" && "text-4xl md:text-5xl font-bold leading-tight",
    className,
  )

  if (as === "span") {
    return (
      <span ref={ref as React.RefObject<HTMLSpanElement>} className={classes} aria-label={children}>
        {content}
      </span>
    )
  }

  const Tag = as
  return (
    <Tag ref={ref as React.RefObject<HTMLDivElement>} style={style} className={classes} aria-label={children}>
      {content}
    </Tag>
  )
}
